/**
 * Le due righe del fotogramma che prima erano un trattino.
 *
 * L'istante e il dettaglio a terra stavano in archivio e la pagina mostrava
 * «—»: il carico non li portava. Ora li porta con la loro origine, e qui si
 * decide che cosa dire accanto al numero — perche' «dal nome del file» vale
 * meno di «dalla camera», e «dall'anagrafica, in pianta» sbaglia di decine
 * di metri rispetto a «dall'anomalia inquadrata». Un trattino resta solo dove
 * il dato manca davvero, e allora dice perche' (GI-245).
 */
import { decimal, moment, notMeasured } from '../format'
import type { MessageKey } from '../i18n/messages'
import type { Frame } from '../types'

export interface Fact {
  value: string
  /** La riga in tono tenue accanto al valore; assente quando non c'e' niente da aggiungere. */
  note: string | null
}

const TAKEN_NOTE: Record<string, MessageKey> = {
  filename: 'photos.takenFromFilename',
  alignment: 'photos.takenFromAlignment',
}

export function takenFact(frame: Frame, t: (key: MessageKey) => string): Fact {
  if (frame.taken_at == null) return { value: notMeasured(), note: t('photos.takenUnknown') }
  const key = frame.taken_at_source == null ? undefined : TAKEN_NOTE[frame.taken_at_source]
  return { value: moment(frame.taken_at), note: key === undefined ? null : t(key) }
}

const QUALITY_NOTE: Record<string, MessageKey> = {
  anomaly: 'photos.qualityFromAnomaly',
  span: 'photos.qualityFromSpan',
  element: 'photos.qualityFromElement',
}

export function qualityFact(
  frame: Frame,
  hasPose: boolean,
  t: (key: MessageKey) => string,
): Fact {
  if (frame.quality == null) {
    return {
      value: notMeasured(),
      note: t(hasPose ? 'photos.qualityNoSubject' : 'photos.qualityNoPose'),
    }
  }
  const key = frame.quality_source == null ? undefined : QUALITY_NOTE[frame.quality_source]
  return { value: `${decimal(frame.quality, 1)} mm/px`, note: key === undefined ? null : t(key) }
}

/**
 * Quanto misura un riquadro sul terreno, dal dettaglio a terra del fotogramma.
 *
 * Il dettaglio (`quality`, mm/px) e' misurato alla distanza del soggetto:
 * larghezza e altezza del riquadro in pixel diventano metri, e l'area metri
 * quadrati. Senza dettaglio non si inventa: si dice che non si misura (GI-259).
 * La precisione e' quella del dettaglio — dal terzo gradino (anagrafica in
 * pianta) e' grossolana, e chi legge lo sa dalla scheda del fotogramma.
 */
export interface Extent {
  width_m: number
  height_m: number
  area_m2: number
}

export function extentOf(box: { width: number; height: number }, gsdMm: number | null | undefined): Extent | null {
  if (gsdMm == null || !(gsdMm > 0) || !(box.width > 0) || !(box.height > 0)) return null
  const width_m = (box.width * gsdMm) / 1000
  const height_m = (box.height * gsdMm) / 1000
  return { width_m, height_m, area_m2: width_m * height_m }
}

export function extentText(extent: Extent): string {
  const side = (value: number): string => (value >= 10 ? decimal(value, 1) : decimal(value, 2))
  return `${side(extent.width_m)} × ${side(extent.height_m)} m · ${decimal(extent.area_m2, extent.area_m2 >= 10 ? 1 : 2)} m²`
}
